import React, { useEffect, useState, useContext, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './orders-details.module.scss'
import { IOrder, IState } from 'src/Interface'
import { StateContext } from '../../AppContext'
import axios from 'axios'
import Map from 'src/components/map/Map'
import { baseApiURL } from 'src/utils/utils'
import { format } from 'date-fns'
import ReactToPrint from 'react-to-print'
import { toast } from 'react-toastify'
import useCancellablePromise from 'src/utils/useCancellablePromise'

interface IProps {
  close?: any
  orderId: number
  referenceLabel: string
}

const OrdersDetails: React.FunctionComponent<IProps> = ({ close, orderId, referenceLabel }: IProps) => {
  const state: IState = useContext(StateContext)
  const [currentOrderId, setCurrentOrderId] = useState(orderId)
  const order: IOrder = state.orders.find((o: any) => o.id === Number(currentOrderId))
  const currentIndex: number = state.orders.findIndex((o: any) => o.id === Number(currentOrderId))
  const { cancellablePromise } = useCancellablePromise()
  const orderDetailsRef = useRef(null)
  const history = useHistory()
  const [geoJson, setGeoJson] = useState(null)


  useEffect(() => {
    if (state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/orders/${currentOrderId}/coordinates`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setGeoJson(result.data);
      }).catch((err) => {
        setGeoJson(null)
        toast(err.response.data.message)
      }))
    }
  }, [currentOrderId, state.user.accessToken, cancellablePromise])

  const cars = useMemo(() => {
    return {
      4: 'Alm. vogn (4 personer)',
      5: 'Stor vogn (5 personer)',
      6: 'Stor vogn (6 personer)',
      7: 'Stor vogn (7 personer)',
      8: 'Stor vogn (8 personer)'
    }
  }, [])

  if (state.orders.length > 0 && !order) {
    history.push('/orders')
  } else if (!order) {
    return null
  }

  return (
    <div ref={orderDetailsRef} className="tw-relative tw-flex tw-flex-col tw-h-full">
      <header className={styles.header}>
        <div className='tw-flex tw-flex-col'>
          <h3>Afsluttet <span className='opacity-60 tw-ml-2'>{format(new Date(order.tripFinishedAt), 'HH:mm')}</span></h3>
          <p className='tw-text-base '>{order.customerName}</p>
        </div>
        <span className={styles.closeIcon} onClick={() => close('/orders')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </header>
      <main className={styles.container}>
        <div className={styles.content}>
          {geoJson && geoJson.features && geoJson.features[0].geometry.coordinates.length > 0 ? <Map geoJson={geoJson} orderId={Number(currentOrderId)} /> :
            <p className='errorMsg'>Intet kortmateriale tilgængeligt</p>}
          <div className='mt-3'>
            <p className={styles.deliveryInfo}>{`${format(new Date(order.tripFinishedAt), 'dd/MM/yy')} | ${format(new Date(order.requestedPickup), 'HH:mm')} - ${format(new Date(order.tripFinishedAt), 'HH:mm')}`}</p>
            <div className={styles.deliveryAddress}>
              <h5 className='tw-flex tw-mb-2 tw-items-center'>
                <svg className='tw-w-4 tw-h-4 fill-grey tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {order.pickupAddress.poi ? order.pickupAddress.poi : order.pickupAddress.fullAddress}</h5>
              {order.destinationAddress && <h5 className='tw-flex tw-items-center'>
                <svg className='tw-w-4 tw-h-4 fill-grey tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                </svg>
                {order.destinationAddress.poi ? order.destinationAddress.poi : order.destinationAddress.fullAddress}
              </h5>}
            </div>
          </div>
          <div className={`${styles.orderInfo} ${!order.comment ? 'border-none' : ''}`}>
            {order.numberOfPeople ? <p>
              <span className={styles.labelLarge}>
                Taxi
              </span>
              {cars[order.numberOfPeople]}
            </p> : null}
            {order.actualTripDuration && <p>
              <span className={styles.labelLarge}>
                Varighed
              </span>
              {Math.round((order.actualTripDuration / 60 + Number.EPSILON) * 100) / 100} min
            </p>}
            {order.finalPrice && <p>
              <span className={styles.labelLarge}>
                Pris
              </span>
              {order.finalPrice} kr
            </p>}
            <p>
              <span className={styles.labelLarge}>
                Betalingsmiddel
              </span>
              {order.account ? 'Konto' : 'I taxi'}
            </p>
          </div>
          {order.comment && <p>
            <span className={`${styles.labelLarge} mt-5`}>Kommentar</span>
            {order.comment}
          </p>}
        </div>
        <aside className={styles.orderDetails}>
          <p>
            <span className={styles.label}>ID</span>
            {order.id ? order.id : '-'}
          </p>
          <p>
            <span className={styles.label}>{referenceLabel}</span>
            {order.reference ? order.reference : '-'}
          </p>
          <p>
            <span className={styles.label}>Dato</span>
            {order.tripFinishedAt ? format(new Date(order.tripFinishedAt), 'dd/MM/yy') : '-'}
          </p>
          <p>
            <span className={styles.label}>Fastpris</span>
            {order.tripType ? order.tripType === 'FIXED' ? 'Ja' : 'Nej' : '-'}
          </p>
          <p>
            <span className={styles.label}>Tilvalg</span>
            {order.attributes.length ? order.attributes.map((attribute, i) => <span key={i} className='block'>{attribute.description}</span>) : '-'}
          </p>
          <p>
            <span className={styles.label}>Telefon</span>
            {order.phone ? order.phone : '-'}
          </p>
          <p>
            <span className={styles.label}>Bestiller</span>
            {order.issuedByEmployee ? order.issuedByEmployee : '-'}
          </p>
        </aside>
      </main>
      <footer className={styles.footer}>
        <div className='tw-flex tw-items-center'>
          <button className={`${styles.navigationButton} ${styles.navigationLeft}`} disabled={!state.orders[currentIndex - 1]} onClick={() => {
            orderDetailsRef.current.scrollTo(0, 0)
            setCurrentOrderId(state.orders[currentIndex - 1].id)
          }}>
            <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          <button
            className={`${styles.navigationButton} ${styles.navigationRight}`}
            disabled={!state.orders[currentIndex + 1]}
            onClick={() => {
              orderDetailsRef.current.scrollTo(0, 0)
              setCurrentOrderId(state.orders[currentIndex + 1].id)
            }}>
            <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <button className={styles.printButton}>
          <ReactToPrint
            trigger={() => <span>Print</span>}
            content={() => orderDetailsRef.current}
          />
        </button>
      </footer >
    </div >
  )
}

export default OrdersDetails
